import { Component } from '@theme/component';
import { morph } from '@theme/morph';

class RelatedProductCard extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {AbortController | null} */
  #dialogAbort = null;
  /** @type {Map<string, Element>} */
  #cache = new Map();
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    super.connectedCallback();
    this.#button = this.querySelector('[data-related-action="open-dialog"]');
    this.#button?.addEventListener('click', this.#handleOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#dialogAbort?.abort();
  }

  #handleOpen = async (event) => {
    event.preventDefault();
    const url = this.dataset.productUrl;
    if (!url || !this.#button) return;
    if (this.#button.dataset.loading === 'true') return;

    let grid = this.#cache.get(url);
    if (!grid) {
      this.#setLoading(true);
      try {
        const fresh = await this.#fetchProductGrid(url);
        if (!fresh) return;
        this.#pruneForDialog(fresh);
        this.#cache.set(url, fresh);
        grid = fresh;
      } finally {
        this.#setLoading(false);
      }
    }

    const dialogEl = document.getElementById('quick-add-dialog');
    const content = document.getElementById('quick-add-modal-content');
    if (!dialogEl || !content || typeof dialogEl.showDialog !== 'function') return;

    morph(content, grid.cloneNode(true));
    this.#wireVariantRaceGuard(dialogEl);
    dialogEl.dataset.openedFrom = 'related-products';
    dialogEl.showDialog();
  };

  /**
   * Prevents adding the wrong variant when the user clicks ADD TO CART
   * before the variant-change fetch completes.
   *
   * Why: `<product-form-component>.#getIntendedVariantId()` falls back to
   * `window.location.href`'s `?variant=` first — which on a PDP is the
   * parent product's variant, not this dialog's. Submitting between
   * `variant:selected` and `variant:update` queues the wrong id.
   *
   * Strategy: while a variant-change fetch is in flight, disable every
   * `<add-to-cart-component>` in the dialog. The variant-update handler
   * inside product-form re-enables based on availability when the fetch
   * resolves.
   *
   * @param {HTMLElement} dialogEl
   */
  #wireVariantRaceGuard(dialogEl) {
    this.#dialogAbort?.abort();
    this.#dialogAbort = new AbortController();
    const { signal } = this.#dialogAbort;

    dialogEl.addEventListener('variant:selected', () => {
      dialogEl.querySelectorAll('add-to-cart-component').forEach((component) => {
        if (typeof component.disable === 'function') component.disable();
      });
    }, { signal });

    dialogEl.addEventListener('dialog:close', () => {
      this.#dialogAbort?.abort();
      this.#dialogAbort = null;
      delete dialogEl.dataset.openedFrom;
    }, { signal, once: true });
  }

  /**
   * Trim the cloned product-details DOM before injecting into the dialog.
   *
   * The actual product page structure inside `.product-details` is:
   *   1. group-block (outer)  — contains .view-product-title + rating stars + inner group-block
   *      └─ inner group-block — contains h1 text-block, product-price, variant-picker
   *   2. shopify-block > .pdp-features  (Weatherproof / 360° cards) — BEFORE the form
   *   3. .buy-buttons-block > product-form-component
   *   4. shopify-block > accordion-custom  (benefits, reviews) — AFTER the form
   *   5. shopify-block > product-recommendations — AFTER the form
   *
   * Strategy (allowlist inside .product-details):
   *   - Keep .group-block that contains .buy-buttons-block, variant-picker,
   *     product-price, or .view-product-title (mirrors the CSS allowlist in
   *     snippets/quick-add-modal-styles.liquid lines 440–458).
   *   - Remove every direct child of .product-details that is NOT one of the
   *     kept group-blocks — this catches .pdp-features and any similar blocks
   *     that sit before the form.
   *   - Then walk up from product-form-component and remove every later sibling
   *     at each ancestor level (catches accordions / recommendations / reviews
   *     that follow the form).
   *   - Finally remove .view-product-title from the kept group-block because the
   *     inner group-block already shows the h1. Also remove .rating-wrapper.
   *
   * @param {Element} grid
   */
  #pruneForDialog(grid) {
    const details = grid.querySelector('.product-details');
    if (details) {
      /** CSS allowlist selectors — a group-block is kept if it contains any of these */
      const KEEP_SELECTORS = [
        '.buy-buttons-block',
        'variant-picker',
        'product-price',
        '.view-product-title',
        'product-inventory',
        '.view-more-details__wrapper',
      ];
      const keepTest = KEEP_SELECTORS.join(', ');

      // Remove every direct child of .product-details that is neither:
      //   a) a group-block containing allowlisted elements, nor
      //   b) the buy-buttons-block span (which wraps product-form-component directly).
      // This removes shopify-blocks like .pdp-features that sit before the form.
      Array.from(details.children).forEach((child) => {
        const isAllowlistedGroupBlock =
          child.classList.contains('group-block') && child.querySelector(keepTest);
        const isBuyButtonsBlock = child.classList.contains('buy-buttons-block');
        if (!isAllowlistedGroupBlock && !isBuyButtonsBlock) {
          child.remove();
        }
      });

      // Inside the surviving group-block(s), strip:
      //  - .view-product-title + .rating-wrapper (dedupes the title; inner h1 stays)
      //  - .pdp-features (Weatherproof / 360° card grid that sits before the form)
      //  - accordion-custom + product-recommendations (in case they're nested deeper
      //    than the "remove later siblings of form" pass reaches)
      // Done at any depth so nested blocks inside the kept group-block are removed.
      details
        .querySelectorAll('.view-product-title, .rating-wrapper, .pdp-features, accordion-custom, product-recommendations')
        .forEach((el) => el.remove());
    }

    // Walk up from product-form-component, removing later siblings at every
    // ancestor level — clears accordions, recommendations, app review blocks.
    const form = grid.querySelector('product-form-component');
    if (!form) return;

    let node = form;
    while (node && node !== grid && node.parentElement) {
      let next = node.nextElementSibling;
      while (next) {
        const toRemove = next;
        next = next.nextElementSibling;
        toRemove.remove();
      }
      node = node.parentElement;
    }
  }

  #setLoading(state) {
    if (!this.#button) return;
    this.#button.dataset.loading = state ? 'true' : 'false';
    this.#button.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  async #fetchProductGrid(url) {
    this.#abort?.abort();
    this.#abort = new AbortController();
    try {
      const response = await fetch(url, { signal: this.#abort.signal });
      if (!response.ok) return null;
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      return doc.querySelector('[data-product-grid-content]');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('related-product-card: failed to fetch product page', error);
      }
      return null;
    } finally {
      this.#abort = null;
    }
  }
}

if (!customElements.get('related-product-card')) {
  customElements.define('related-product-card', RelatedProductCard);
}
